import React, {useEffect, useMemo, useRef, useState} from "react";
import {useIntegrator} from "../../integrator.context";
import {useApp} from "../../app.context";
import {useAccess} from "../../utils/useAccess";
import ResizeBar, {useResizable} from "../../elements/resizeBar";
import {usePaletteTab} from "../../elements/paletteTabButton";
import MapCanvas, {MapCanvasHandle, MapEdit, MapTool} from "./mapCanvas";
import MapPalette from "./mapPalette";
import {useMapUndo} from "./mapUndo";
import {
    compactPalette, computeMapMetadata, createMapDraft, englishMapName, findMapNameClash, getMapDisplayName,
    mapCopyName, MapDocument, MapManifestEntry, MAP_SIZE_PRESETS, MAP_SPAWNS_MAX, MAP_SPAWNS_MIN,
    paletteImageAt, paletteIndexOf, resizeMapDocument, resourceAllowedAt, sanitizeMapDocument,
} from "../../models/map.model";
import {
    deleteMapRequest, demoteMapRequest, fetchLiveMapList, fetchMap, fetchMapList, promoteMapRequest, publishMap,
} from "../../utils/maps.api";
import {ImageOverrides, loadImageOverrides, pickImageKey, saveImageOverrides} from "./mapImagePrefs";

// The Map Editor: author playable maps (terrain grid + resources + spawn
// cities) against the current branch's tiles/resources, publish them to the
// versioned 'maps' blob store, and load them back. Maps are branch-agnostic
// documents — switching branches re-resolves sprites and warnings, never
// destroys data. The working draft lives in localStorage, not branch state,
// so it neither dirties the branch nor rides its undo history (the editor
// keeps its own stroke-level undo, see mapUndo).

const DRAFT_STORAGE_KEY = 'rift.editor.mapDraft';

// Load Map list columns: name, version, size, spawns, water, live. The header and
// every row render from this one template so the columns actually line up. The name
// takes the slack; the numeric columns are fixed so the digits align down the list
// rather than drifting with content. The id is only on the name's tooltip.
const MAP_LIST_GRID = 'minmax(0, 1fr) 64px 72px 64px 56px 64px';
const mapListHeaderCell = {color: 'var(--muted-text-color)', fontSize: 12, fontWeight: 600} as const;
// Long names clip with an ellipsis rather than pushing the grid wide; the full
// value is on the cell's title tooltip.
const mapListClipCell = {overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'} as const;
// Data cells inherit the row's white; only the header and a genuinely unknown
// value are muted. Colour in this table means something — the tint on the
// selected name and on 'live', amber on a stale live version — so greying the
// ordinary values just made the real signals harder to pick out.
const mapListNumberCell = {whiteSpace: 'nowrap', textAlign: 'right'} as const;

function loadDraft(): MapDocument | undefined {
    try {
        return sanitizeMapDocument(JSON.parse(localStorage.getItem(DRAFT_STORAGE_KEY) ?? ''));
    } catch { /* corrupt/absent */ }
    return undefined;
}

function MapEditorView() {
    const {state, branch, getBranchVersionNumber, totalChanges} = useIntegrator();
    const {confirm, choose, setNotification, getLocalizationValue} = useApp();
    const {isReadOnly, canPublishMap, canDeleteMap, canPromoteMap} = useAccess();

    const containerRef = useRef<HTMLDivElement>(null);
    const canvasHandleRef = useRef<MapCanvasHandle>(null);
    const [doc, setDoc] = useState<MapDocument | undefined>(loadDraft);
    const [tool, setTool] = useState<MapTool>('paint');
    // Owned here (not in the palette) so picking a tool can bring the
    // matching palette tab forward.
    const [paletteTab, setPaletteTab] = usePaletteTab('rift.editor.mapsPaletteTab', 'Tiles');
    // Bold-grid-line cadence (view preference; slider in the Map tab).
    const [gridSize, _setGridSize] = useState<number>(() => {
        const stored = Number(localStorage.getItem('rift.editor.mapGridSize'));
        return Number.isInteger(stored) && stored >= 2 && stored <= 40 ? stored : 8;
    });

    function setGridSize(size: number) {
        localStorage.setItem('rift.editor.mapGridSize', String(size));
        _setGridSize(size);
    }

    // Governs only the bold cadence — the per-cell grid is always drawn. The storage
    // key keeps its old name deliberately, so nobody's saved preference is reset by
    // the rename.
    const [boldGridEnabled, _setBoldGridEnabled] = useState<boolean>(
        () => localStorage.getItem('rift.editor.mapGridShown') !== 'false');

    function setBoldGridEnabled(enabled: boolean) {
        localStorage.setItem('rift.editor.mapGridShown', String(enabled));
        _setBoldGridEnabled(enabled);
    }
    const [selectedTileId, setSelectedTileId] = useState<string | undefined>(undefined);
    const [selectedResourceId, setSelectedResourceId] = useState<string | undefined>(undefined);
    // The palette's eraser row. Kept separate from selectedResourceId rather than
    // encoded as a sentinel id, so no fake id can reach an edit — and so the previous
    // resource selection survives a trip through the eraser.
    const [eraseResources, setEraseResources] = useState(false);
    const [hoverCell, setHoverCell] = useState<{x: number; y: number} | undefined>(undefined);
    const [showHelp, setShowHelp] = useState(false);
    const [newDialog, setNewDialog] = useState(false);
    const [loadDialog, setLoadDialog] = useState<{entries: MapManifestEntry[]; filter: string; selected?: string} | undefined>(undefined);
    // Map name -> the version players currently have. undefined means the live
    // manifest could not be read (including on a backend older than getLiveMaps),
    // and is rendered as "unknown" — a failed lookup must never read as "not live".
    const [liveVersions, setLiveVersions] = useState<Record<string, number> | undefined>(undefined);
    // The maps published to dev, for the duplicate-name check. Empty until fetched,
    // so a failed fetch only leaves the check to the backend.
    const [devMaps, setDevMaps] = useState<MapManifestEntry[]>([]);
    const [deleteArmed, setDeleteArmed] = useState(false);
    const deleteArmTimer = useRef<number | undefined>(undefined);
    const undoStack = useMapUndo();
    // Per-entity "displayed image" choices (see mapImagePrefs) — shared by
    // the palette rows and the canvas so they always show the same sprite.
    const [imageOverrides, setImageOverrides] = useState<ImageOverrides>(loadImageOverrides);

    // An empty selection is stored as no entry at all: "nothing chosen" and "chose
    // nothing" behave identically (paint falls back to the pack default), so there's
    // no reason to keep both states.
    function setEntityImages(entityId: string, keys: string[]) {
        setImageOverrides(prev => {
            const next = {...prev};
            if (keys.length > 0) next[entityId] = keys;
            else delete next[entityId];
            saveImageOverrides(next);
            return next;
        });
    }

    const palette = useResizable({
        storageKey: 'rift.editor.mapsPaletteWidth',
        axis: 'x', min: 260, invert: true,
        initial: () => Math.round(window.innerWidth * 0.22),
        max: () => (containerRef.current?.getBoundingClientRect().width ?? Infinity) - 360,
    });

    const docRef = useRef(doc);
    docRef.current = doc;

    useEffect(() => () => window.clearTimeout(deleteArmTimer.current), []);

    // The Map tab shows live state for the open document, so this can't wait for
    // the load picker to be opened.
    useEffect(() => {
        void refreshLiveVersions();
        void refreshDevMaps();
        // Mount only; promote/withdraw keep the lookup current thereafter.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // First open with no draft: start a default-size map once the branch's
    // tiles are available (the grid needs a tile to fill with).
    useEffect(() => {
        if (doc !== undefined) return;
        const firstTile = state.tiles?.find(t => t.active);
        if (firstTile) persistDraft(createMapDraft(16, firstTile.id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doc, state.tiles]);

    function persistDraft(next: MapDocument) {
        setDoc(next);
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(next));
    }

    // Resource lists compared by content, not order: the rebuild above moves painted
    // cells to the end, so a plain length/identity check would call every no-op stroke
    // a change and fill the undo stack with them.
    function sameResources(
        a: {x: number; y: number; id: string; image?: string}[],
        b: {x: number; y: number; id: string; image?: string}[],
    ): boolean {
        if (a.length !== b.length) return false;
        const key = (r: {x: number; y: number; id: string; image?: string}) =>
            `${r.x},${r.y},${r.id},${r.image ?? ''}`;
        const left = new Set(a.map(key));
        return b.every(r => left.has(key(r)));
    }

    // ---- edits (one undo entry per committed stroke) -----------------------

    function applyEdit(edit: MapEdit) {
        const current = docRef.current;
        if (!current || isReadOnly) return;
        switch (edit.kind) {
            case 'paintTiles': {
                // A palette entry is the (tileId, pinned image) combo — painting the
                // same tile with a different variant appends a new combo rather than
                // repainting existing cells' variants.
                //
                // Resolved per CELL, because one stroke can carry several variants
                // (the brush is a selection and each cell drew its own pick), so a
                // single stroke may append several palette entries.
                let tilePalette = current.tilePalette;
                let tilePaletteImages = current.tilePaletteImages;
                const tileGrid = [...current.tileGrid];

                for (const cell of edit.cells) {
                    let paletteIndex = paletteIndexOf(
                        {...current, tilePalette, tilePaletteImages}, edit.tileId, cell.image);
                    if (paletteIndex < 0) {
                        const previousLength = tilePalette.length;
                        tilePalette = [...tilePalette, edit.tileId];
                        if (tilePaletteImages || cell.image) {
                            tilePaletteImages = tilePaletteImages
                                ? [...tilePaletteImages]
                                : new Array(previousLength).fill(null);
                            tilePaletteImages.push(cell.image ?? null);
                        }
                        paletteIndex = tilePalette.length - 1;
                    }
                    tileGrid[cell.y * current.size + cell.x] = paletteIndex;
                }

                undoStack.record(current);
                persistDraft({...current, tilePalette, tilePaletteImages, tileGrid});
                return;
            }
            case 'paintResources': {
                // At most one resource per cell, so the stroke's cells replace whatever
                // was there — or clear it when the eraser is the brush (id undefined).
                const painted = new Map<string, {x: number; y: number; id: string; image?: string}>();
                for (const cell of edit.cells) {
                    if (edit.id === undefined) continue;
                    painted.set(`${cell.x},${cell.y}`, {
                        x: cell.x, y: cell.y, id: edit.id,
                        ...(cell.image ? {image: cell.image} : {}),
                    });
                }

                const touched = new Set(edit.cells.map(cell => `${cell.x},${cell.y}`));
                const resources = current.resources
                    .filter(r => !touched.has(`${r.x},${r.y}`))
                    .concat(Array.from(painted.values()));

                // Nothing to record when the stroke changed nothing — erasing empty
                // cells, or repainting exactly what was already there.
                if (sameResources(current.resources, resources)) return;

                undoStack.record(current);
                persistDraft({...current, resources});
                return;
            }
            case 'toggleSpawn': {
                const existing = current.spawns.find(s => s.x === edit.x && s.y === edit.y);
                if (!existing && current.spawns.length >= MAP_SPAWNS_MAX) {
                    setNotification({title: 'Spawn Limit', message: `Maps support at most ${MAP_SPAWNS_MAX} spawns.`});
                    return;
                }
                const spawns = existing
                    ? current.spawns.filter(s => s !== existing)
                    : [...current.spawns, {x: edit.x, y: edit.y}];
                undoStack.record(current);
                persistDraft({...current, spawns});
                return;
            }
        }
    }

    function updateDocProperties(mutate: (doc: MapDocument) => void) {
        const current = docRef.current;
        if (!current || isReadOnly) return;
        const next = {...current, displayNames: {...current.displayNames}};
        mutate(next);
        persistDraft(next);
    }

    async function resizeDoc(newSize: number) {
        const current = docRef.current;
        if (!current || isReadOnly) return;
        if (newSize < current.size) {
            const proceed = await confirm({
                title: 'Shrink map?',
                body: <p>Shrinking to <strong>{newSize}×{newSize}</strong> crops tiles, resources and spawns
                    outside the new bounds. Continue?</p>,
                confirmText: 'Shrink',
                danger: true,
            });
            if (!proceed) return;
        }
        undoStack.record(current);
        persistDraft(resizeMapDocument(current, newSize));
    }

    // ---- undo/redo (feature-local, active only while this pane is visible) --
    // Deliberately separate from the ruleset history: the sidebar's undo/redo
    // never touches map drafts, and these never touch branch data.

    function performUndo() {
        const current = docRef.current;
        if (!current) return;
        const next = undoStack.undo(current);
        if (next) persistDraft(next);
    }

    function performRedo() {
        const current = docRef.current;
        if (!current) return;
        const next = undoStack.redo(current);
        if (next) persistDraft(next);
    }

    // Stable across renders via refs so the mount-only listener stays fresh.
    const performUndoRef = useRef(performUndo);
    performUndoRef.current = performUndo;
    const performRedoRef = useRef(performRedo);
    performRedoRef.current = performRedo;

    useEffect(() => {
        function onKey(e: KeyboardEvent) {
            if (!(e.ctrlKey || e.metaKey) || (e.key !== 'z' && e.key !== 'Z' && e.key !== 'y')) return;
            // Pane hidden (display:none ancestor) → offsetParent is null.
            if (!containerRef.current || containerRef.current.offsetParent === null) return;
            const t = e.target as HTMLElement;
            if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t.isContentEditable) return;
            e.preventDefault();
            const redo = e.key === 'y' || (e.shiftKey && (e.key === 'z' || e.key === 'Z'));
            if (redo) performRedoRef.current();
            else performUndoRef.current();
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const nameClash = doc ? findMapNameClash(englishMapName(doc), devMaps, doc.name) !== undefined : false;

    // ---- warnings (soft — only the name ones block publish) -----------------

    const {warnings, warningCells} = useMemo(() => {
        const warnings: string[] = [];
        const warningCells = new Set<string>();
        if (!doc) return {warnings, warningCells};

        const tileById = new Map(state.tiles.map(t => [t.id, t]));
        const resourceById = new Map(state.resources.map(r => [r.id, r]));

        const usedPalette = new Set(doc.tileGrid);
        doc.tilePalette.forEach((id, index) => {
            if (!usedPalette.has(index)) return;
            const tile = tileById.get(id);
            if (tile && tile.active) return;
            warnings.push(tile
                ? `Tile "${id}" is inactive in this branch — the map won't load against it`
                : `Tile "${id}" doesn't exist in this branch — the map won't load against it`);
            for (let i = 0; i < doc.tileGrid.length; i++) {
                if (doc.tileGrid[i] === index) warningCells.add(`${i % doc.size},${Math.floor(i / doc.size)}`);
            }
        });

        // Pinned variant images that don't resolve in this branch — soft:
        // the game falls back to a random variant, but the author should know.
        const missingVariantImages = new Set<string>();
        const imageResolves = (key: string) => state.images && key in state.images;
        doc.tilePalette.forEach((_, index) => {
            if (!usedPalette.has(index)) return;
            const image = paletteImageAt(doc, index);
            if (!image || imageResolves(image)) return;
            missingVariantImages.add(image);
            for (let i = 0; i < doc.tileGrid.length; i++) {
                if (doc.tileGrid[i] === index) warningCells.add(`${i % doc.size},${Math.floor(i / doc.size)}`);
            }
        });
        for (const resource of doc.resources) {
            if (resource.image && !imageResolves(resource.image)) {
                missingVariantImages.add(resource.image);
                warningCells.add(`${resource.x},${resource.y}`);
            }
        }
        missingVariantImages.forEach(key =>
            warnings.push(`Variant image "${key}" doesn't exist in this branch — the game will show a random variant`));

        const missingResources = new Set<string>();
        const disallowedResources = new Map<string, number>();
        for (const resource of doc.resources) {
            const data = resourceById.get(resource.id);
            if (!data || !data.active) {
                missingResources.add(resource.id);
                warningCells.add(`${resource.x},${resource.y}`);
                continue;
            }
            if (!resourceAllowedAt(doc, state.tiles ?? [], resource.x, resource.y, resource.id)) {
                disallowedResources.set(resource.id, (disallowedResources.get(resource.id) ?? 0) + 1);
                warningCells.add(`${resource.x},${resource.y}`);
            }
        }
        missingResources.forEach(id =>
            warnings.push(`Resource "${id}" doesn't exist (or is inactive) in this branch`));
        disallowedResources.forEach((count, id) =>
            warnings.push(`Resource "${id}" sits on ${count} tile(s) that don't allow it in this branch`));

        for (const spawn of doc.spawns) {
            const tile = tileById.get(doc.tilePalette[doc.tileGrid[spawn.y * doc.size + spawn.x]]);
            if (tile && (tile.canSail || !tile.canMove)) {
                warnings.push(`Spawn at (${spawn.x}, ${spawn.y}) is on ${tile.canSail ? 'water' : 'an impassable tile'} — the client will reject it`);
                warningCells.add(`${spawn.x},${spawn.y}`);
            }
        }

        if (doc.spawns.length < MAP_SPAWNS_MIN)
            warnings.push(`Maps need at least ${MAP_SPAWNS_MIN} spawns to be playable (currently ${doc.spawns.length})`);
        if (nameClash)
            warnings.push(`Another map is already called "${englishMapName(doc)}" — rename this one to publish (Map tab)`);
        else if (englishMapName(doc) === '')
            warnings.push('The map needs a name to publish (Map tab)');

        return {warnings, warningCells};
    }, [doc, state, nameClash]);

    // ---- publish / load / new ----------------------------------------------

    async function publishFlow() {
        const current = docRef.current;
        if (!current) return;
        const name = englishMapName(current);
        const blockers: string[] = [];
        if (name === '') blockers.push('a name');
        if (current.spawns.length < MAP_SPAWNS_MIN) blockers.push(`at least ${MAP_SPAWNS_MIN} spawns`);
        if (blockers.length > 0) {
            setNotification({title: 'Not Ready to Publish', message: `The map needs ${blockers.join(', ')}. See the Map tab.`});
            return;
        }
        // Against a fresh list: another author may have taken the name since.
        const clash = findMapNameClash(name, await refreshDevMaps() ?? devMaps, current.name);
        if (clash) {
            setNotification({title: 'Name Taken', message: `Another map is already called "${englishMapName(clash)}". Rename this one in the Map tab.`});
            return;
        }

        const publishDoc = compactPalette(current);
        publishDoc.displayNames.en = name;
        // A never-published draft goes without an id and the backend mints one from
        // the name, so a new map can never land on top of an existing one.
        if (current.versionNumber === 0) publishDoc.name = '';
        publishDoc.metadata = computeMapMetadata(publishDoc, state.tiles);
        publishDoc.authoredAgainst = {branch, versionNumber: getBranchVersionNumber(branch)};

        const nextVersion = current.versionNumber + 1;
        const proceed = await confirm({
            title: 'Publish map to dev?',
            body: <div className={'layout vertical'} style={{gap: 6}}>
                <p>Publish <strong>{getMapDisplayName(publishDoc)}</strong> as version <strong>{nextVersion >= 1 ? nextVersion : 1}</strong>?</p>
                <p className={'muted-text'}>Playable straight away in the <strong>dev client</strong>. Live players
                    don't get it until you promote it — Map tab, Live.</p>
                <p className={'muted-text'}>Published versions are immutable — games pin the version they started
                    on, so this never affects games in progress.</p>
                {totalChanges > 0 &&
                    <p className={'warning-text'}>This branch has unexported changes; the map's tile and resource ids
                        are validated against exported version {getBranchVersionNumber(branch)}.</p>}
                {warnings.length > 0 &&
                    <p className={'warning-text'}>{`${warnings.length} authoring warning(s) — check the Map tab.`}</p>}
            </div>,
            confirmText: 'Publish',
        });
        if (!proceed) return;

        const result = await publishMap(publishDoc);
        if ('error' in result) {
            setNotification({title: 'Publish Failed', message: result.error});
            return;
        }
        persistDraft({
            ...current, name: result.name, versionNumber: result.versionNumber,
            displayNames: publishDoc.displayNames, authoredAgainst: publishDoc.authoredAgainst,
        });
        void refreshDevMaps();
        setNotification({
            title: 'Map Published to Dev',
            message: `"${getMapDisplayName(publishDoc)}" is dev v${result.versionNumber} — playable now in the dev client. Promote it in the Map tab to reach live players.`,
        });
    }

    // Refreshes the name -> live version lookup. Left undefined on failure so the
    // UI can say "unknown" instead of claiming nothing is live.
    async function refreshLiveVersions() {
        const live = await fetchLiveMapList();
        if (live === undefined) {
            setLiveVersions(undefined);
            return;
        }
        const byName: Record<string, number> = {};
        for (const entry of live) byName[entry.name] = entry.versionNumber;
        setLiveVersions(byName);
    }

    // Returns the list too, for callers that need it before the next render.
    async function refreshDevMaps(): Promise<MapManifestEntry[] | undefined> {
        const entries = await fetchMapList();
        if (entries !== undefined) setDevMaps(entries);
        return entries;
    }

    async function showLoadPicker() {
        // Both manifests together: what is authored here, and what players have.
        const [entries] = await Promise.all([refreshDevMaps(), refreshLiveVersions()]);
        if (entries === undefined) {
            setNotification({title: 'Load Failed', message: 'Could not reach map storage.'});
            return;
        }
        if (entries.length === 0) {
            setNotification({title: 'No Maps', message: 'No maps published to the editor (dev) yet.'});
            return;
        }
        const sorted = [...entries].sort((a, b) =>
            getMapDisplayName(a).localeCompare(getMapDisplayName(b), undefined, {numeric: true, sensitivity: 'base'}));
        disarmDelete();
        setLoadDialog({entries: sorted, filter: '', selected: undefined});
    }

    // Both flows take the bare identity rather than a manifest entry, so the Map
    // tab can drive them from the loaded document and the picker from a row.
    async function promoteFlow(target: {name: string; versionNumber: number; displayName: string}) {
        const liveVersion = liveVersions?.[target.name];
        const proceed = await confirm({
            title: 'Promote map to LIVE SERVICE',
            body: <div className={'layout vertical'} style={{gap: 6}}>
                <p>Put <strong>{target.displayName}</strong> version <strong>{target.versionNumber}</strong> in
                    front of <strong>live players</strong>?</p>
                {liveVersion !== undefined
                    ? <p className={'muted-text'}>Live currently has version {liveVersion}; it will be replaced.
                        Games already running on version {liveVersion} pin it and keep working.</p>
                    : <p className={'muted-text'}>This map is not live yet.</p>}
            </div>,
            confirmText: 'Promote to LIVE SERVICE',
            danger: true,
        });
        if (!proceed) return;

        const result = await promoteMapRequest(target.name, target.versionNumber);
        if (!result.success) {
            setNotification({title: 'Promote Failed', message: result.error ?? 'Could not promote the map.'});
            return;
        }
        setLiveVersions(v => ({...(v ?? {}), [target.name]: target.versionNumber}));
        setNotification({
            title: 'Map Promoted',
            message: `"${target.displayName}" v${target.versionNumber} is now live.`,
        });
    }

    async function demoteFlow(target: {name: string; displayName: string}) {
        const proceed = await confirm({
            title: 'Withdraw map from LIVE SERVICE',
            body: <div className={'layout vertical'} style={{gap: 6}}>
                <p>Remove <strong>{target.displayName}</strong> from the live map list?</p>
                <p className={'muted-text'}>Live players stop seeing it in new games; it stays published to dev and
                    playable in the dev client. Games already running on it pin their version and keep working, and
                    promoting it again later restores it.</p>
            </div>,
            confirmText: 'Withdraw from LIVE SERVICE',
            danger: true,
        });
        if (!proceed) return;

        const result = await demoteMapRequest(target.name);
        if (!result.success) {
            setNotification({title: 'Withdraw Failed', message: result.error ?? 'Could not withdraw the map.'});
            return;
        }
        setLiveVersions(v => {
            if (v === undefined) return v;
            const next = {...v};
            delete next[target.name];
            return next;
        });
        setNotification({title: 'Map Withdrawn', message: `"${target.displayName}" is no longer live.`});
    }

    async function loadMapByName(name: string) {
        const loaded = await fetchMap(name);
        if (!loaded) {
            setNotification({title: 'Load Failed', message: `Could not load map "${name}".`});
            return;
        }
        setLoadDialog(undefined);
        persistDraft(loaded);
        undoStack.reset();
    }

    async function loadFlow() {
        const current = docRef.current;
        if (current && current.versionNumber === 0 && englishMapName(current) !== '') {
            const action = await choose({
                title: 'Replace current draft?',
                body: <p>The current draft <strong>{englishMapName(current)}</strong> has never been published.
                    Loading another map will discard it.</p>,
                options: [
                    {text: 'Discard draft', value: 'discard'},
                    {text: 'Cancel', value: 'cancel'},
                ],
            });
            if (action !== 'discard') return;
        }
        await showLoadPicker();
    }

    function disarmDelete() {
        window.clearTimeout(deleteArmTimer.current);
        setDeleteArmed(false);
    }

    function onDeleteClick(name: string) {
        if (!deleteArmed) {
            setDeleteArmed(true);
            window.clearTimeout(deleteArmTimer.current);
            deleteArmTimer.current = window.setTimeout(() => setDeleteArmed(false), 3000);
            return;
        }
        disarmDelete();
        void performDelete(name);
    }

    async function performDelete(name: string) {
        const result = await deleteMapRequest(name);
        if (!result.success) {
            setNotification({title: 'Delete Failed', message: result.error ?? `Could not delete map "${name}".`});
            return;
        }
        setLoadDialog(d => d ? {...d, entries: d.entries.filter(e => e.name !== name), selected: undefined} : d);
        setDevMaps(entries => entries.filter(e => e.name !== name));
    }

    // Save as: the current map, unpublished edits included, becomes a new draft with no
    // id, so publishing it creates a separate map and leaves the original as it is.
    // Translations are left behind, since they would still name the original.
    function duplicateMap() {
        const current = docRef.current;
        if (!current || current.versionNumber === 0) return;
        persistDraft({
            ...current,
            name: '',
            versionNumber: 0,
            displayNames: {en: mapCopyName(getMapDisplayName(current), devMaps)},
            authoredAgainst: undefined,
            updatedAtUtc: undefined,
        });
        undoStack.reset();
        activatePaletteTab('Map');
    }

    function newMap(size: number) {
        const firstTile = state.tiles?.find(t => t.active);
        if (!firstTile) return;
        setNewDialog(false);
        persistDraft(createMapDraft(size, firstTile.id));
        undoStack.reset();
    }

    // ---- render -------------------------------------------------------------

    if (!doc) {
        return <div className={'layout vertical center'} style={{flexGrow: 1, padding: 40}}>
            <p className={'muted-text'}>Waiting for branch data (the map needs a tile to start from)...</p>
        </div>;
    }

    const hoverTileId = hoverCell
        ? doc.tilePalette[doc.tileGrid[hoverCell.y * doc.size + hoverCell.x]]
        : undefined;
    const hoverResource = hoverCell
        ? doc.resources.find(r => r.x === hoverCell.x && r.y === hoverCell.y)
        : undefined;

    // Labelled (icon + text) so each tool is discoverable even if an icon
    // glyph is missing from the FontAwesome kit. Picking a tool brings the
    // palette tab it draws from forward.
    function activateTool(target: MapTool) {
        setTool(target);
        if (target === 'paint' || target === 'fill') setPaletteTab('Tiles');
        else if (target === 'resource') setPaletteTab('Resources');
    }

    // The inverse of activateTool: opening Tiles means the user intends to paint,
    // opening Resources to place resources, so the tool follows the tab.
    //
    // Two deliberate restrictions, both to protect Fill. This is NOT an effect on
    // paletteTab, because activateTool moves the tab itself — reacting to every tab
    // change would bounce Fill straight back to Paint the moment it was picked. And it
    // only acts when the tab actually CHANGES, so re-clicking the tab you're already
    // on doesn't take Fill away from you either (PaletteTabButton fires on every
    // click, including the active one). Tabs with no tool of their own, like Map,
    // leave the current tool alone.
    function activatePaletteTab(tab: string) {
        const changed = tab !== paletteTab;
        setPaletteTab(tab);
        if (!changed) return;

        // setTool, not activateTool: the tab is already being set right here, so
        // going through activateTool would only write it a second time.
        if (tab === 'Tiles') setTool('paint');
        else if (tab === 'Resources') setTool('resource');
    }

    function toolButton(target: MapTool, icon: string, label: string, title: string) {
        return <button title={title}
                       onClick={() => activateTool(target)}
                       disabled={isReadOnly}
                       className={'layout horizontal center'}
                       style={{
                           gap: 6, flexShrink: 0, whiteSpace: 'nowrap',
                           ...(tool === target ? {color: 'var(--tint-color)', borderColor: 'var(--tint-color)'} : {}),
                       }}>
            <span className={`fa-solid ${icon}`}/>
            {label}
        </button>;
    }

    return (
        <div ref={containerRef} className={'layout horizontal'} style={{flexGrow: 1, minHeight: 0}}>
            <div className={'layout vertical box'} style={{flexGrow: 1, minWidth: 0, minHeight: 0, padding: 10, gap: 8}}>
                {/* Wraps at narrow widths: the button bar drops to its own row
                    rather than colliding with the title, and wraps internally
                    rather than spilling over the palette. */}
                <div className={'layout horizontal center stretch wrap'} style={{columnGap: 10, rowGap: 8}}>
                    {/* Title + warning count live on the left so the button
                        bar's position never shifts when warnings appear. */}
                    <div className={'layout horizontal'} style={{gap: 10, alignItems: 'center', minWidth: 0}}>
                        {/* Truncates rather than pushing the button bar aside. */}
                        <p style={{fontWeight: 600, whiteSpace: 'nowrap', minWidth: 0,
                            overflow: 'hidden', textOverflow: 'ellipsis'}}>
                            Map Editor
                            <span className={'muted-text'}>
                                {` — ${englishMapName(doc) || 'untitled'}${doc.versionNumber > 0 ? ` (v${doc.versionNumber})` : ' (draft)'}`}
                            </span>
                        </p>
                        {warnings.length > 0 &&
                            <p className={'warning-text'} title={warnings.join('\n')}
                               style={{whiteSpace: 'nowrap', flexShrink: 0}}>
                                {`${warnings.length} warning${warnings.length === 1 ? '' : 's'}`}
                            </p>}
                    </div>
                    {/* flexGrow keeps the bar right-aligned both when it shares
                        the row with the title and when it wraps below it. */}
                    <div className={'layout horizontal center wrap'}
                         style={{columnGap: 10, rowGap: 8, flexGrow: 1, justifyContent: 'flex-end'}}>
                        {/* Map-draft history only — the sidebar's undo/redo is
                            the ruleset's and never applies here. */}
                        <button title={'Undo painting (Ctrl+Z)'}
                                onClick={performUndo}
                                disabled={isReadOnly || !undoStack.canUndo}
                                style={{opacity: undoStack.canUndo ? 1 : 0.5}}>
                            <span className={'fa-solid fa-undo'}/>
                        </button>
                        <button title={'Redo painting (Ctrl+Shift+Z)'}
                                onClick={performRedo}
                                disabled={isReadOnly || !undoStack.canRedo}
                                style={{opacity: undoStack.canRedo ? 1 : 0.5}}>
                            <span className={'fa-solid fa-redo'}/>
                        </button>
                        <span style={{width: 6}}/>
                        {toolButton('paint', 'fa-paintbrush', 'Paint', 'Paint tiles (click/drag)')}
                        {toolButton('fill', 'fa-fill-drip', 'Fill', 'Flood fill connected tiles')}
                        {toolButton('resource', 'fa-gem', 'Resource', 'Place/remove resources (pick one in the palette)')}
                        {toolButton('spawn', 'fa-flag', 'Spawn', 'Toggle spawn cities (click cells)')}
                        <span style={{width: 6}}/>
                        {/* Labelled like the tool buttons — the icon alone read as
                            "file" rather than "start a new map". */}
                        <button title={'New map'}
                                onClick={() => setNewDialog(true)}
                                disabled={isReadOnly}
                                className={'layout horizontal center'}
                                style={{gap: 6, flexShrink: 0, whiteSpace: 'nowrap'}}>
                            <span className={'fa-solid fa-file'}/>
                            New Map
                        </button>
                        <button title={doc.versionNumber > 0
                                    ? 'Start a new map from this one — the original stays as it is'
                                    : 'Publish this map first: an unpublished draft is already a new map'}
                                onClick={duplicateMap}
                                disabled={isReadOnly || doc.versionNumber === 0}
                                className={'layout horizontal center'}
                                style={{gap: 6, flexShrink: 0, whiteSpace: 'nowrap', opacity: doc.versionNumber > 0 ? 1 : 0.5}}>
                            <span className={'fa-solid fa-copy'}/>
                            Duplicate
                        </button>
                        <button title={'Load a map published to the editor (dev)'} onClick={() => void loadFlow()}>
                            <span className={'fa-solid fa-folder-open'}/>
                        </button>
                        {canPublishMap &&
                            <button title={'Publish a new version — playable in the dev client; promote it in the Map tab to reach live players'}
                                    onClick={() => void publishFlow()}>
                                <span className={'fa-solid fa-cloud-arrow-up'}/>
                            </button>}
                        <button title={'Zoom to fit the map'} onClick={() => canvasHandleRef.current?.zoomToFit()}>
                            <span className={'fa-solid fa-expand'}/>
                        </button>
                        <div style={{position: 'relative'}}>
                            <button title={'Help'} onClick={() => setShowHelp(!showHelp)}>
                                <span className={'fa-solid fa-circle-question'}/>
                            </button>
                            {showHelp &&
                                <div className={'layout vertical box'}
                                     style={{
                                         position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                                         // Clamped so a narrow window can't push
                                         // the popup off the left of the screen.
                                         width: 560, maxWidth: 'calc(100vw - 40px)',
                                         padding: 12, gap: 6, zIndex: 100,
                                     }}>
                                    <p style={{fontWeight: 600}}>Map Editor controls</p>
                                    <p className={'muted-text'}>• Pick a tile in the palette, then left-click/drag to paint</p>
                                    <p className={'muted-text'}>• Fill tool: repaint a connected region in one click</p>
                                    <p className={'muted-text'}>• Alt + click: pick the tile under the cursor (eyedropper)</p>
                                    <p className={'muted-text'}>• Resource tool: click to place; click the same resource again to remove</p>
                                    <p className={'muted-text'}>• Spawn tool: click to toggle spawn cities (numbered)</p>
                                    <p className={'muted-text'}>• Ctrl+Z / Ctrl+Shift+Z: undo / redo painting</p>
                                    <p className={'muted-text'}>• Right-drag: pan · wheel/pinch/middle-drag: zoom</p>
                                    <p className={'muted-text'}>• Red-tinted cells have warnings — see the Map tab</p>
                                </div>}
                        </div>
                    </div>
                </div>
                <div style={{flexGrow: 1, minHeight: 0, position: 'relative'}}>
                    <MapCanvas ref={canvasHandleRef}
                               doc={doc}
                               tool={tool}
                               selectedTileId={selectedTileId}
                               selectedResourceId={selectedResourceId}
                               eraseResources={eraseResources}
                               warningCells={warningCells}
                               gridSize={gridSize}
                               boldGridEnabled={boldGridEnabled}
                               // Always a concrete pack image (brush override
                               // else pack default) — every paint pins a
                               // variant so the map renders identically for
                               // everyone who loads it.
                               pickImage={entityId => pickImageKey(state, imageOverrides, entityId)}
                               onEdit={applyEdit}
                               onPickTile={(id, image) => {
                                   setSelectedTileId(id);
                                   setEntityImages(id, image ? [image] : []);
                                   activateTool('paint');
                               }}
                               onHoverCell={setHoverCell}/>
                    {/* The hovered cell, overlaid at a fixed size: in the toolbar it
                        made the bar wrap to a second row and the canvas jump. */}
                    {hoverCell &&
                        <p className={'muted-text box'}
                           style={{
                               position: 'absolute', top: 8, right: 8, width: 240, height: 24,
                               padding: '0 8px', lineHeight: '24px', fontSize: 12, textAlign: 'right',
                               overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                               pointerEvents: 'none', opacity: 0.9,
                           }}>
                            {`(${hoverCell.x}, ${hoverCell.y}) ${hoverTileId ? getLocalizationValue(hoverTileId) : ''}${hoverResource ? ` + ${getLocalizationValue(hoverResource.id)}` : ''}`}
                        </p>}
                </div>
            </div>

            <ResizeBar axis={'x'} {...palette.handleProps}/>

            <div className={'layout vertical'} style={{width: palette.size, flexShrink: 0}}>
                <MapPalette doc={doc}
                            tab={paletteTab}
                            onSetTab={activatePaletteTab}
                            selectedTileId={selectedTileId}
                            selectedResourceId={selectedResourceId}
                            warnings={warnings}
                            nameClash={nameClash}
                            onSelectTile={id => {
                                setSelectedTileId(id);
                                if (tool !== 'fill') setTool('paint');
                            }}
                            onSelectResource={id => {
                                setSelectedResourceId(id);
                                // Picking a real resource leaves erase mode.
                                setEraseResources(false);
                                setTool('resource');
                            }}
                            eraseResources={eraseResources}
                            onSetEraseResources={erase => {
                                setEraseResources(erase);
                                if (erase) setTool('resource');
                            }}
                            imageOverrides={imageOverrides}
                            onSetEntityImages={setEntityImages}
                            onUpdateDoc={updateDocProperties}
                            onResize={size => void resizeDoc(size)}
                            gridSize={gridSize}
                            onSetGridSize={setGridSize}
                            boldGridEnabled={boldGridEnabled}
                            onSetBoldGridEnabled={setBoldGridEnabled}
                            liveKnown={liveVersions !== undefined}
                            liveVersion={liveVersions?.[doc.name]}
                            canPromote={canPromoteMap}
                            onPromote={() => void promoteFlow(
                                {name: doc.name, versionNumber: doc.versionNumber, displayName: getMapDisplayName(doc)})}
                            onWithdraw={() => void demoteFlow(
                                {name: doc.name, displayName: getMapDisplayName(doc)})}/>
            </div>

            {newDialog && (
                <div className={'layout vertical overlay'} style={{zIndex: 1500}}>
                    <div className={'layout vertical gap box'} style={{padding: 12, gap: 10, width: 360}}>
                        <p className={'text header'}>New Map</p>
                        <p className={'muted-text'}>This replaces the current draft. Pick a size:</p>
                        <div className={'layout horizontal gap'} style={{flexWrap: 'wrap'}}>
                            {MAP_SIZE_PRESETS.map(size => (
                                <button key={size} onClick={() => newMap(size)}>{`${size}×${size}`}</button>
                            ))}
                        </div>
                        <div className={'layout horizontal'} style={{justifyContent: 'flex-end', gap: 8}}>
                            <button onClick={() => setNewDialog(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {loadDialog && (() => {
                const filter = loadDialog.filter.trim().toLowerCase();
                const visible = loadDialog.entries.filter(e => filter === ''
                    || e.name.toLowerCase().includes(filter)
                    || getMapDisplayName(e).toLowerCase().includes(filter));
                const selected = loadDialog.selected !== undefined && visible.some(e => e.name === loadDialog.selected)
                    ? loadDialog.selected : undefined;
                const enterTarget = selected ?? (visible.length === 1 ? visible[0].name : undefined);
                return (
                    <div className={'layout vertical overlay'} style={{zIndex: 1500}}>
                        {/* Wider than the other dialogs: six columns of map
                            metadata don't fit in 520px without the name
                            collapsing to a few characters. */}
                        <div className={'layout vertical gap box'} style={{padding: 12, gap: 10, width: 'min(880px, 92vw)'}}>
                            <p className={'text header'}>Load Map</p>
                            {/* Says up front which environment this list is, since
                                every other column describes the dev copy. */}
                            <p className={'muted-text'} style={{fontSize: 12}}>
                                Published to dev and playable in the dev client. <strong>Live</strong> is the
                                version live players have.
                            </p>
                            <input autoFocus placeholder={'Filter...'}
                                   value={loadDialog.filter}
                                   onChange={e => setLoadDialog({...loadDialog, filter: e.target.value})}
                                   onKeyDown={e => {
                                       e.stopPropagation();
                                       if (e.key === 'Enter' && enterTarget !== undefined) void loadMapByName(enterTarget);
                                       if (e.key === 'Escape') setLoadDialog(undefined);
                                   }}/>
                            <div className={'layout vertical'}
                                 style={{maxHeight: 'min(80vh, calc(100vh - 260px))', minHeight: 160, overflowY: 'auto', gap: 2}}>
                                {/* Header lives INSIDE the scroller, stuck to the top.
                                    Outside it, a scrollbar would narrow the rows but not
                                    the header and the columns would drift apart. It shares
                                    one grid template with every row — same pattern as
                                    usersEditor — so the columns actually line up. */}
                                <div style={{
                                    display: 'grid', gridTemplateColumns: MAP_LIST_GRID, gap: 8,
                                    padding: '2px 8px', position: 'sticky', top: 0, zIndex: 1,
                                    backgroundColor: 'var(--box-color)',
                                }}>
                                    <span style={mapListHeaderCell}>Name</span>
                                    <span style={{...mapListHeaderCell, textAlign: 'right'}}>Version</span>
                                    <span style={{...mapListHeaderCell, textAlign: 'right'}}>Size</span>
                                    <span style={{...mapListHeaderCell, textAlign: 'right'}}>Spawns</span>
                                    <span style={{...mapListHeaderCell, textAlign: 'right'}}>Water</span>
                                    <span style={mapListHeaderCell}>Live</span>
                                </div>
                                {visible.length === 0 &&
                                    <p className={'muted-text'} style={{fontStyle: 'italic', padding: 4}}>
                                        No maps match the filter.
                                    </p>}
                                {visible.map(entry => {
                                    const displayName = getMapDisplayName(entry);
                                    const liveVersion = liveVersions?.[entry.name];
                                    // What players actually have. "not live" is the state that
                                    // explains a map you published never showing up in the game;
                                    // "?" is a failed lookup, which must not read as "not live".
                                    const live = liveVersions === undefined
                                        // Only a genuinely unknown value stays muted.
                                        ? <span className={'muted-text'}>?</span>
                                        : liveVersion === undefined
                                            ? <span>not live</span>
                                            : liveVersion === entry.versionNumber
                                                ? <span style={{color: 'var(--tint-color)'}}>live</span>
                                                : <span className={'warning-text'}>{`v${liveVersion}`}</span>;
                                    return (
                                        <div key={entry.name}
                                             onClick={() => {
                                                 disarmDelete();
                                                 setLoadDialog({...loadDialog, selected: entry.name});
                                             }}
                                             onDoubleClick={() => void loadMapByName(entry.name)}
                                             style={{
                                                 display: 'grid', gridTemplateColumns: MAP_LIST_GRID, gap: 8,
                                                 alignItems: 'center', cursor: 'pointer', padding: '4px 8px',
                                                 borderRadius: 4, userSelect: 'none', color: 'white',
                                                 backgroundColor: entry.name === selected ? 'rgba(255,255,255,0.12)' : undefined,
                                             }}>
                                            <span style={{...mapListClipCell, color: entry.name === selected ? 'var(--tint-color)' : undefined}}
                                                  title={`${displayName}\nID: ${entry.name}`}>
                                                {displayName}
                                            </span>
                                            <span style={mapListNumberCell}>{`v${entry.versionNumber}`}</span>
                                            <span style={mapListNumberCell}>{`${entry.size}×${entry.size}`}</span>
                                            <span style={mapListNumberCell}>{entry.metadata?.spawnCount ?? '?'}</span>
                                            <span style={mapListNumberCell}>
                                                {`${Math.round((entry.metadata?.waterFraction ?? 0) * 100)}%`}
                                            </span>
                                            <span style={{whiteSpace: 'nowrap'}}>{live}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className={'layout horizontal center stretch'} style={{gap: 8}}>
                                {canDeleteMap && <button disabled={selected === undefined}
                                        // Delete tombstones the DEV manifest only, so a
                                        // live map stays live — say so rather than let
                                        // "disappears from listings" imply otherwise.
                                        title={deleteArmed
                                            ? 'Click again to delete from the dev list — existing games keep working'
                                            : selected !== undefined && liveVersions?.[selected] !== undefined
                                                ? 'Removes it from the dev list only — this map stays LIVE until you withdraw it'
                                                : 'Remove the selected map from the dev list'}
                                        onBlur={disarmDelete}
                                        style={{
                                            opacity: selected === undefined ? 0.5 : 1,
                                            ...(deleteArmed ? {backgroundColor: '#a03030', color: 'white'} : {}),
                                        }}
                                        onClick={() => selected !== undefined && onDeleteClick(selected)}>
                                    {deleteArmed ? 'Delete?' : 'Delete'}
                                </button>}
                                {canPromoteMap && (() => {
                                    const entry = visible.find(e => e.name === selected);
                                    const liveVersion = entry && liveVersions?.[entry.name];
                                    const alreadyLive = entry !== undefined && liveVersion === entry.versionNumber;
                                    return (
                                        <div className={'layout horizontal'} style={{gap: 8}}>
                                            <button disabled={entry === undefined || alreadyLive}
                                                    style={{opacity: entry === undefined || alreadyLive ? 0.5 : 1}}
                                                    title={alreadyLive
                                                        ? 'This version is already live'
                                                        : 'Put the selected version in front of live players'}
                                                    onClick={() => entry && void promoteFlow(
                                                        {name: entry.name, versionNumber: entry.versionNumber, displayName: getMapDisplayName(entry)})}>
                                                Promote to Live
                                            </button>
                                            <button disabled={entry === undefined || liveVersion === undefined}
                                                    style={{opacity: entry === undefined || liveVersion === undefined ? 0.5 : 1}}
                                                    title={'Remove the selected map from the live map list'}
                                                    onClick={() => entry && void demoteFlow(
                                                        {name: entry.name, displayName: getMapDisplayName(entry)})}>
                                                Withdraw
                                            </button>
                                        </div>
                                    );
                                })()}
                                <div className={'layout horizontal'} style={{gap: 8}}>
                                    <button onClick={() => setLoadDialog(undefined)}>Cancel</button>
                                    <button disabled={selected === undefined}
                                            style={{opacity: selected === undefined ? 0.5 : 1}}
                                            onClick={() => selected !== undefined && void loadMapByName(selected)}>
                                        Load
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

export default MapEditorView;
